/**
 * LessonAudiobookMode — full-page "distraction-free listening" overlay
 * for a lesson. Streams an MP3 from /api/tts/synthesize, offers voice /
 * speed picker, and shows the lesson text as a large-print teleprompter
 * that scrolls with playback.
 *
 * Renders itself only when `open === true`. Parent (LessonViewer) owns
 * the toggle button.
 */
import React, { useState, useEffect, useRef, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Play, Pause, RotateCcw, Headphones, Volume2 } from 'lucide-react';

const SPEED_OPTIONS = [0.75, 1.0, 1.25, 1.5, 1.75, 2.0];

export default function LessonAudiobookMode({ open, onClose, lessonId, lessonTitle, lessonText }) {
  const { API } = useContext(AuthContext);
  const [voices, setVoices] = useState([]);
  const [voice, setVoice] = useState('fable');
  const [speed, setSpeed] = useState(1.0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const audioRef = useRef(null);

  // Fetch the voice catalog once — hides premium voices for free users.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tts/voices`);
        setVoices(data.voices || []);
        setVoice(data.default || 'fable');
      } catch { /* soft-fail — pickers stay hidden */ }
    })();
  }, [open, API]);

  // Auto-fetch audio the first time the modal opens (and whenever voice/
  // speed changes). Uses a blob URL so the browser can seek/scrub. We
  // now always route through the background-job pipeline so 30 min+
  // lessons never race Cloudflare's 60 s edge timeout.
  const synthesise = async () => {
    if (!lessonText || !lessonText.trim()) {
      toast.error('This lesson has no readable text');
      return;
    }
    setLoading(true);
    setPlaying(false);
    setProgress(0);
    setProgressLabel('Queuing audio job…');
    if (audioUrl) {
      try { URL.revokeObjectURL(audioUrl); } catch { /* ignore */ }
    }
    try {
      // 1) start the background job
      const startRes = await axios.post(
        `${API}/tts/synthesize/start`,
        { text: lessonText, voice, speed, lesson_id: lessonId },
      );
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error('No job id returned');

      // 2) poll for status. Cached jobs come back "done" on the first hit.
      let attempts = 0;
      let job = startRes.data;
      while (job.status !== 'done' && job.status !== 'failed') {
        if (attempts > 240) throw new Error('TTS job timed out after 6 min');
        await new Promise((r) => setTimeout(r, 1500));
        attempts += 1;
        try {
          const jr = await axios.get(`${API}/tts/synthesize/job/${jobId}`);
          job = jr.data;
          if (job.chunks_total) {
            setProgressLabel(
              `Synthesizing… ${job.chunks_done || 0}/${job.chunks_total} chunks`,
            );
          } else {
            setProgressLabel('Preparing…');
          }
        } catch (pollErr) {
          // Transient poll errors are OK — swallow up to 5 in a row.
          if (attempts % 6 === 0) {
            console.warn('TTS poll transient error', pollErr);
          }
        }
      }
      if (job.status === 'failed') {
        throw new Error(job.error || 'TTS synthesis failed');
      }

      // 3) download the finished MP3
      setProgressLabel('Downloading audio…');
      const res = await axios.get(
        `${API}/tts/synthesize/download/${jobId}`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'audio/mpeg' }));
      setAudioUrl(url);
      setProgressLabel('');
      setTimeout(() => {
        try {
          audioRef.current?.play();
          setPlaying(true);
        } catch { /* browsers may block auto-play; user hits Play */ }
      }, 100);
    } catch (e) {
      setProgressLabel('');
      toast.error(
        e?.response?.data?.detail ||
          e?.message ||
          'Audio generation failed — try a shorter lesson or a different voice.',
        { duration: 8000 },
      );
    } finally {
      setLoading(false);
    }
  };

  // Kick off the first synth automatically when the modal opens.
  useEffect(() => {
    if (open && !audioUrl && !loading) {
      synthesise();
    }
    // eslint-disable-next-line
  }, [open]);

  // When voice or speed changes AFTER the initial load, generate a
  // fresh cut. Skip the very first paint (no audioUrl yet) to avoid a
  // double-fetch race with the mount effect above.
  useEffect(() => {
    if (open && audioUrl) synthesise();
    // eslint-disable-next-line
  }, [voice, speed]);

  useEffect(() => {
    return () => {
      if (audioUrl) {
        try { URL.revokeObjectURL(audioUrl); } catch { /* ignore */ }
      }
    };
  }, [audioUrl]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  const restart = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = 0;
    audioRef.current.play();
    setPlaying(true);
  };

  const fmt = (s) => {
    if (!Number.isFinite(s)) return '0:00';
    const mm = Math.floor(s / 60);
    const ss = Math.floor(s % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-slate-950 text-slate-100"
      data-testid="audiobook-mode"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-800">
        <div className="flex items-center gap-2 min-w-0">
          <Headphones className="w-5 h-5 text-amber-400 shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-400">Audiobook Mode</div>
            <div className="text-sm font-semibold truncate">{lessonTitle}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-slate-400 hover:text-white flex items-center gap-1 text-sm"
          data-testid="audiobook-close"
        >
          <X className="w-5 h-5" /> Exit
        </button>
      </div>

      {/* Body — large-print teleprompter of the lesson text */}
      <div className="flex-1 overflow-y-auto px-6 py-10 flex justify-center">
        <div className="max-w-3xl w-full">
          <p className="text-xl leading-relaxed whitespace-pre-wrap text-slate-200 font-serif">
            {lessonText || 'This lesson has no readable text to synthesise.'}
          </p>
        </div>
      </div>

      {/* Sticky player bar */}
      <div className="border-t border-slate-800 bg-slate-900/95 backdrop-blur px-6 py-4">
        <div className="max-w-4xl mx-auto space-y-3">
          {/* Progress bar */}
          <div className="flex items-center gap-3">
            <span className="text-xs tabular-nums text-slate-400 w-12">{fmt(progress)}</span>
            <input
              type="range"
              min="0"
              max={duration || 0}
              step="0.1"
              value={progress}
              onChange={(e) => {
                const t = Number(e.target.value);
                if (audioRef.current) audioRef.current.currentTime = t;
                setProgress(t);
              }}
              disabled={!audioUrl || loading}
              className="flex-1 accent-amber-400"
              data-testid="audiobook-scrub"
            />
            <span className="text-xs tabular-nums text-slate-400 w-12 text-right">{fmt(duration)}</span>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={restart}
                disabled={!audioUrl || loading}
                className="w-10 h-10 rounded-full border border-slate-700 hover:border-amber-400 flex items-center justify-center disabled:opacity-40"
                title="Restart"
                data-testid="audiobook-restart"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={togglePlay}
                disabled={!audioUrl || loading}
                className="w-14 h-14 rounded-full bg-amber-400 text-slate-950 hover:bg-amber-300 flex items-center justify-center disabled:opacity-40"
                data-testid="audiobook-play-pause"
              >
                {loading ? (
                  <span className="text-[10px] font-bold uppercase animate-pulse text-center leading-tight">
                    {progressLabel || 'Loading'}
                  </span>
                ) : playing ? (
                  <Pause className="w-6 h-6" />
                ) : (
                  <Play className="w-6 h-6 ml-0.5" />
                )}
              </button>
            </div>

            <div className="flex items-center gap-4 flex-wrap">
              {/* Voice picker */}
              <label className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-slate-300">
                <Volume2 className="w-4 h-4" /> Voice
                <select
                  value={voice}
                  onChange={(e) => setVoice(e.target.value)}
                  disabled={loading}
                  className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm font-normal normal-case tracking-normal"
                  data-testid="audiobook-voice-picker"
                >
                  {voices.length === 0 ? (
                    <option value="fable">Fable</option>
                  ) : (
                    voices.map((v) => (
                      <option key={v.id} value={v.id} disabled={!v.available}>
                        {v.name} — {v.description}{v.premium ? (v.available ? ' ★' : ' 🔒') : ''}
                      </option>
                    ))
                  )}
                </select>
              </label>

              {/* Speed picker */}
              <div className="flex items-center gap-1">
                {SPEED_OPTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSpeed(s)}
                    disabled={loading}
                    className={`text-xs px-2 py-1 rounded ${
                      speed === s
                        ? 'bg-amber-400 text-slate-950 font-bold'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                    data-testid={`audiobook-speed-${s}`}
                  >
                    {s}×
                  </button>
                ))}
              </div>
            </div>
          </div>

          <audio
            ref={audioRef}
            src={audioUrl || undefined}
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
            onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            data-testid="audiobook-audio-el"
          />
        </div>
      </div>
    </div>
  );
}
